import { createBrowserRouter, Navigate } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import { InboxPage } from '@platform/features/users/inbox/InboxPage';
import { ModuleDesignPage } from '@platform/features/modules/ModuleDesignPage';
import { ModulePage } from '@platform/features/modules/ModulePage';
import { ModuleSettingsPage } from '@platform/features/modules/ModuleSettingsPage';
import { SearchPage } from '@platform/features/search/SearchPage';
import { bindEntry } from './auth/bindEntry';
import { entryRoutes } from './auth/entryRoutes';
import { AdminHomePage } from './pages/admin/AdminHomePage';
import { AdminSectionPage } from './pages/admin/AdminSectionPage';
import { AllModulesPage } from './pages/AllModulesPage';
import { AreaPage } from './pages/area/AreaPage';
import { BiPage } from './pages/bi/BiPage';
import { CustomizeHomePage } from './pages/customize/CustomizeHomePage';
import { CustomizeSectionPage } from './pages/customize/CustomizeSectionPage';
import { FavouritesPage } from './pages/FavouritesPage';
import { HomePage } from './pages/HomePage';
import { InvitationsPage } from './pages/members/InvitationsPage';
import { MembersPage } from './pages/members/MembersPage';
import { ProfilePage } from './pages/ProfilePage';
import { RecentPage } from './pages/RecentPage';
import { HomeViewPage } from './pages/work/HomeViewPage';
import { EntityOrPageRoute } from './routing/EntityOrPageRoute';
import { LegacyRedirect } from './routing/LegacyRedirect';
import { ModuleRoute } from './routing/ModuleRoute';
import { RecordOrViewRoute } from './routing/RecordOrViewRoute';
import { RecordTabRoute } from './routing/RecordTabRoute';
import { WorkspaceLayout } from './WorkspaceLayout';

/**
 * Маршрути (06-Frontend §5, ADR-028): адреса без робочого простору (він — у стані сесії вкладки, WorkspaceLayout). Сторінки оболонки — від
 * кореня (inbox, search, work/:view, admin/:section, customize/:section[/:item]…), огляд напряму — `/:group`, модуль — `/:group/:module`
 * (пакет вантажиться ліниво на межі ModuleRoute) з settings/:item, design/:item і сегментами: сутність або сторінка модуля, далі запис (GUID)
 * чи подання, далі вкладка запису. Статичні сегменти мають перевагу над `:group`, тож slug груп не збігаються з ними (тест маршрутів).
 * Старі адреси `/w/:ws/*` переспрямовує LegacyRedirect. Екрани входу, реєстрації, «Створити робочий простір» (/onboarding) і спостерігача —
 * entryRoutes (src/app/auth), поза оболонкою, як екрани прототипу.
 * Поведінка React Router v7 увімкнена заздалегідь (future-прапорці роутера; v7_startTransition — у RouterProvider, App.tsx): застосунок
 * не має splat-маршрутів із відносними посиланнями, fetcher-ів, action-ів і SSR-гідратації, тож прапорці нічого не ламають.
 */
export const router = createBrowserRouter(
  [
    ...entryRoutes,
    { path: '/w/:ws/*', element: <LegacyRedirect /> },
    {
      path: routes.root(),
      element: <WorkspaceLayout />,
      children: [
        { index: true, element: <HomePage /> },
        { path: 'inbox', element: <InboxPage /> },
        { path: 'search', element: <SearchPage /> },
        { path: 'modules', element: <AllModulesPage /> },
        { path: 'recent', element: <RecentPage /> },
        { path: 'favourites', element: <FavouritesPage /> },
        { path: 'work/:view', element: <HomeViewPage /> },
        { path: 'bi/:view', element: <BiPage /> },
        { path: 'members', element: <MembersPage /> },
        { path: 'members/invitations', element: <InvitationsPage /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'admin', element: <AdminHomePage /> },
        { path: 'admin/:section', element: <AdminSectionPage /> },
        { path: 'customize', element: <CustomizeHomePage /> },
        { path: 'customize/:section', element: <CustomizeSectionPage /> },
        { path: 'customize/:section/:item', element: <CustomizeSectionPage /> },
        { path: ':group', element: <AreaPage /> },
        {
          path: ':group/:module',
          element: <ModuleRoute />,
          children: [
            { index: true, element: <ModulePage /> },
            { path: 'settings', element: <ModuleSettingsPage /> },
            { path: 'settings/:item', element: <ModuleSettingsPage /> },
            { path: 'design', element: <ModuleDesignPage /> },
            { path: 'design/:item', element: <ModuleDesignPage /> },
            { path: ':entityOrPage', element: <EntityOrPageRoute /> },
            { path: ':entityOrPage/:idOrView', element: <RecordOrViewRoute /> },
            { path: ':entityOrPage/:idOrView/:tab', element: <RecordTabRoute /> },
          ],
        },
      ],
    },
    { path: '*', element: <Navigate to={routes.root()} replace /> },
  ],
  {
    basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/',   // база збірки Vite (APP_BASE)
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

bindEntry(router);
